import referralApi from "../api/referral";

export const VISITOR_ID_KEY = "solarent_visitor_id";
export const REFERRAL_INFO_KEY = "solarent_referral_info";
export const REFERRAL_COOKIE_NAME = "solarent_ref";
export const REFERRAL_SESSION_TRACK_PREFIX = "solarent_referral_tracked_";

const isDev = process.env.NODE_ENV === "development";

function safeParseJson(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** WordPress/marketing site — reserved; not used for displayed referral links in pilot. */
export function getPublicMarketingBaseUrl() {
  const fromConfig = window.globalConfig?.PUBLIC_MARKETING_BASE_URL;
  if (fromConfig) {
    return String(fromConfig).trim().replace(/\/$/, "");
  }

  const fromEnv = process.env.REACT_APP_PUBLIC_MARKETING_BASE_URL;
  if (fromEnv) {
    return String(fromEnv).trim().replace(/\/$/, "");
  }

  return "https://solarent.ir";
}

/** React app base for referral short links: https://app.solarent.ir/r/{code} */
export function getFrontendAppBaseUrl() {
  const fromConfig = window.globalConfig?.FRONTEND_APP_BASE_URL;
  if (fromConfig) {
    return String(fromConfig).trim().replace(/\/$/, "");
  }

  const fromEnv = process.env.REACT_APP_FRONTEND_APP_BASE_URL;
  if (fromEnv) {
    return String(fromEnv).trim().replace(/\/$/, "");
  }

  if (isDev && typeof window !== "undefined") {
    return window.location.origin.replace(/\/$/, "");
  }

  return "https://app.solarent.ir";
}

export function buildAppReferralLink(referralCode) {
  const code = (referralCode || "").trim();
  if (!code) return "";
  return `${getFrontendAppBaseUrl()}/r/${encodeURIComponent(code)}`;
}

/** Sub-partner recruitment link: {FrontendAppBaseUrl}/p/{code}/join */
export function buildPartnerJoinLink(referralCode) {
  const code = (referralCode || "").trim();
  if (!code) return "";
  return `${getFrontendAppBaseUrl()}/p/${encodeURIComponent(code)}/join`;
}

/** @deprecated Use buildAppReferralLink — kept for existing imports. */
export function buildMarketingReferralLink(referralCode) {
  return buildAppReferralLink(referralCode);
}

export function buildReferralShareText(referralCode) {
  const link = buildAppReferralLink(referralCode);
  if (!link) return "";
  return `با لینک من در سولارنت ثبت‌نام کنید و درخواست نیروگاه خورشیدی خود را ثبت کنید:\n${link}`;
}

/**
 * Optional cookie from future WordPress integration (.solarent.ir).
 * Pilot referral links use app.solarent.ir/r/{code} — see docs/WORDPRESS_REFERRAL_REDIRECT.md.
 */
export function getReferralCookie() {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${REFERRAL_COOKIE_NAME}=([^;]*)`)
  );
  if (!match?.[1]) {
    return null;
  }

  try {
    const value = decodeURIComponent(match[1]).trim();
    return value || null;
  } catch {
    return null;
  }
}

export function getOrCreateVisitorId() {
  try {
    const existing = window.localStorage.getItem(VISITOR_ID_KEY);
    if (existing) {
      return existing;
    }

    let visitorId;
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      visitorId = crypto.randomUUID();
    } else {
      visitorId = `v-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }

    window.localStorage.setItem(VISITOR_ID_KEY, visitorId);
    return visitorId;
  } catch {
    return `v-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
}

export function getReferralCodeFromUrl(search = window.location.search) {
  const params = new URLSearchParams(search);
  const ref = params.get("ref") || params.get("referralCode");
  const code = (ref || "").trim();
  return code || null;
}

export function getReferralCodeFromRoute(pathname = window.location.pathname) {
  const match = String(pathname || "").match(/^\/r\/([^/?#]+)/i);
  if (!match?.[1]) {
    return null;
  }

  try {
    const code = decodeURIComponent(match[1]).trim();
    return code || null;
  } catch {
    return null;
  }
}

export function resolveReferralCode({
  search = window.location.search,
  pathname = window.location.pathname,
  includeCookie = true,
} = {}) {
  return (
    getReferralCodeFromUrl(search) ||
    getReferralCodeFromRoute(pathname) ||
    (includeCookie ? getReferralCookie() : null) ||
    null
  );
}

function getUtmParamsFromUrl(search = window.location.search) {
  const params = new URLSearchParams(search);
  return {
    utmSource: params.get("utm_source") || params.get("utmSource") || null,
    utmMedium: params.get("utm_medium") || params.get("utmMedium") || null,
    utmCampaign: params.get("utm_campaign") || params.get("utmCampaign") || null,
    campaignId: params.get("campaignId") ? Number(params.get("campaignId")) : null,
    channel: params.get("channel") || null,
  };
}

export function getStoredReferral() {
  try {
    const raw = window.localStorage.getItem(REFERRAL_INFO_KEY);
    if (!raw) {
      return null;
    }
    return safeParseJson(raw);
  } catch {
    return null;
  }
}

export function storeReferralInfo(info) {
  if (!info) {
    return;
  }
  try {
    window.localStorage.setItem(REFERRAL_INFO_KEY, JSON.stringify(info));
  } catch {
    // ignore storage errors
  }
}

export function storeReferralFallback(referralCode) {
  const code = (referralCode || "").trim();
  if (!code) {
    return;
  }

  storeReferralInfo({
    referralCode: code,
    partnerId: null,
    expiresAt: null,
    attributionId: null,
    status: "PendingTrack",
  });
}

function wasTrackedThisSession(referralCode) {
  try {
    return (
      window.sessionStorage.getItem(`${REFERRAL_SESSION_TRACK_PREFIX}${referralCode}`) ===
      "1"
    );
  } catch {
    return false;
  }
}

function markTrackedThisSession(referralCode) {
  try {
    window.sessionStorage.setItem(`${REFERRAL_SESSION_TRACK_PREFIX}${referralCode}`, "1");
  } catch {
    // ignore
  }
}

export function clearReferralIfExpired() {
  const stored = getStoredReferral();
  if (!stored?.expiresAt) {
    return;
  }

  const expiresAt = new Date(stored.expiresAt).getTime();
  if (!Number.isNaN(expiresAt) && expiresAt < Date.now()) {
    try {
      window.localStorage.removeItem(REFERRAL_INFO_KEY);
    } catch {
      // ignore
    }
  }
}

export function hasActiveReferral() {
  clearReferralIfExpired();
  const stored = getStoredReferral();
  if (stored?.referralCode) {
    return true;
  }
  return Boolean(resolveReferralCode());
}

export function getReferralFieldsForSignup() {
  const visitorId = getOrCreateVisitorId();
  const stored = getStoredReferral();
  const referralCode =
    getReferralCodeFromUrl() ||
    getReferralCodeFromRoute() ||
    stored?.referralCode ||
    getReferralCookie() ||
    null;

  const payload = { visitorId };
  if (referralCode) {
    payload.referralCode = referralCode;
  }
  return payload;
}

export function getVisitorIdForRequest() {
  return getOrCreateVisitorId();
}

/**
 * After mobile OTP login/signup, links visitor attribution to the authenticated user.
 * Safe to call; never throws.
 */
export async function attachReferralRegistrationIfNeeded() {
  clearReferralIfExpired();
  const visitorId = getOrCreateVisitorId();
  const stored = getStoredReferral();
  const referralCode =
    stored?.referralCode || getReferralCodeFromUrl() || getReferralCookie();

  if (!visitorId || !referralCode) {
    return null;
  }

  try {
    const response = await referralApi.attachRegistration({ visitorId });
    const isSuccess = response?.isSuccess ?? response?.IsSuccess;
    if (isDev && !isSuccess) {
      console.warn("[referral] attach-registration:", response?.message || response?.Message);
    }
    return response;
  } catch (error) {
    if (isDev) {
      console.warn("[referral] attach-registration failed:", error?.message || error);
    }
    return null;
  }
}

/**
 * Calls POST /api/referral/track. Safe to call when ref is present in URL.
 * Does not throw; logs warning in development on failure.
 */
export async function trackReferralVisit(options = {}) {
  const referralCode =
    options.referralCode ||
    resolveReferralCode({
      search: options.search,
      pathname: options.pathname,
      includeCookie: options.includeCookie !== false,
    });

  if (!referralCode) {
    return null;
  }

  if (!options.force && wasTrackedThisSession(referralCode)) {
    return getStoredReferral();
  }

  const visitorId = getOrCreateVisitorId();
  const utm = getUtmParamsFromUrl(options.search);

  try {
    const response = await referralApi.trackReferral({
      referralCode,
      visitorId,
      landingUrl: options.landingUrl || window.location.href,
      utmSource: utm.utmSource,
      utmMedium: utm.utmMedium,
      utmCampaign: utm.utmCampaign,
      campaignId: utm.campaignId,
      channel: utm.channel,
    });

    const isSuccess = response?.isSuccess ?? response?.IsSuccess;
    const data = response?.data ?? response?.Data;

    if (isSuccess && data) {
      storeReferralInfo({
        referralCode: data.referralCode || data.ReferralCode || referralCode,
        partnerId: data.partnerId ?? data.PartnerId ?? null,
        expiresAt: data.expiresAt ?? data.ExpiresAt ?? null,
        attributionId: data.attributionId ?? data.AttributionId ?? null,
        status: data.status ?? data.Status ?? null,
      });
      markTrackedThisSession(referralCode);
      return data;
    }

    storeReferralFallback(referralCode);
    markTrackedThisSession(referralCode);

    const message = response?.message || response?.Message;
    if (isDev && message) {
      console.warn("[referral] track:", message);
    }
    return null;
  } catch (error) {
    storeReferralFallback(referralCode);
    markTrackedThisSession(referralCode);
    if (isDev) {
      console.warn("[referral] track failed:", error?.message || error);
    }
    return null;
  }
}
